import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { storageService } from '../services/storage.service';
import { authService } from '../services/auth.service';
import { AppSettings } from '../types/data';
import { SYNC_CONFIG, API_CONFIG } from '../config/app.config';
import { useLang } from '../contexts/LangContext';
import { syncService, SyncStatus } from '../services/sync.service';
import { APP_CONFIG } from '../config/app.config';
import { downloadBackupFile, pickBackupFile, readBackupFile } from '../utils/backupRestore';
import { storageManager, StorageType } from '../services/storage.manager';
import ModalWindow from '../components/ModalWindow';
import { motion } from 'framer-motion';

const SettingsPage: React.FC = () => {
  const { t, setLang } = useLang();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pinEnabled, setPinEnabled] = useState(false);
  const [pinLoading, setPinLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncError, setSyncError] = useState<string>('');
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [storageType, setStorageType] = useState<StorageType>('localStorage');
  const [storageSwitching, setStorageSwitching] = useState(false);
  const [modalChildren, setModalChildren] = useState<React.ReactNode>(null);
  const [tapCount, setTapCount] = useState(0);
  const [tapTimer, setTapTimer] = useState<NodeJS.Timeout | null>(null);
  const [surveySettingsExpanded, setSurveySettingsExpanded] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    loadSettings();
    loadPinStatus();
    loadStorageType();
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      setCurrentUser(user);
    } catch {
      setCurrentUser(null);
    }
  };

  useEffect(() => {
    let mounted = true;

    syncService.getSyncStatus().then((s) => {
      if (mounted) setSyncStatus(s);
    }).catch(() => {
      // ignore
    });

    const unsubscribe = syncService.subscribe((s) => {
      setSyncStatus(s);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const loadSettings = async () => {
    try {
      const data = await storageService.getSettings();
      // console.log('[SettingsPage] Loaded settings:', { offlineMode: data.offlineMode, settings: data });
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPinStatus = async () => {
    try {
      const enabled = await authService.isPinEnabled();
      setPinEnabled(enabled);
    } catch (error) {
      console.error('Error loading PIN status:', error);
    } finally {
      setPinLoading(false);
    }
  };

  const handleTogglePin = async (nextEnabled: boolean) => {
    if (nextEnabled) {
      // Enabling requires setting a PIN
      navigate('/setup-pin');
      return;
    }

    const confirmed = window.confirm(
      t('settings_confirmDisablePin')
    );
    if (!confirmed) return;

    try {
      await authService.disablePin();
      setPinEnabled(false);
    } catch (error) {
      console.error('Error disabling PIN:', error);
    }
  };

  const loadStorageType = () => {
    const currentType = storageManager.getCurrentStorageType();
    setStorageType(currentType);
  };

  const handleVersionTap = () => {
    if (tapTimer) {
      clearTimeout(tapTimer);
    }

    const newCount = tapCount + 1;
    setTapCount(newCount);

    if (newCount === 3) {
      // Triple tap detected - show easter egg modal
      setModalChildren(
        <div className="text-center bg-white bg-opacity-80 backdrop-blur rounded-lg mx-scr py-4">
          <h3 className="text-3xl app-header font-semibold text-blue-700 mb-4">{t("appTitle")}</h3>
          <p className="text-gray-700 mb-2">Developed by <strong><a href="https://github.com/tlabsim" target="_blank" rel="noopener noreferrer">tlabsim</a></strong></p>
          <p className="text-blue-600 mb-2">tlabs.im@gmail.com</p>
          <a 
            href="http://www.tlabsinc.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            www.tlabsinc.com
          </a>
        </div>
      );
      setTapCount(0);
      setTapTimer(null);
    } else {
      // Reset count after 500ms if not tapped again
      const timer = setTimeout(() => {
        setTapCount(0);
        setTapTimer(null);
      }, 500);
      setTapTimer(timer);
    }
  };

  const handleStorageTypeChange = async (newType: StorageType) => {
    if (newType === storageType) return;

    const confirmed = window.confirm(
      `Switch storage from ${storageType} to ${newType}? This will migrate all your data. This operation may take a few moments.`
    );
    if (!confirmed) return;

    setStorageSwitching(true);
    try {
      const result = await storageManager.switchStorageType(newType);
      
      if (result.success) {
        setStorageType(newType);
        alert(`Successfully switched to ${newType} storage!`);
        // Reload to ensure everything is using the new storage
        window.location.reload();
      } else {
        alert(`Failed to switch storage: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error switching storage type:', error);
      alert(`Error: ${error.message || 'Failed to switch storage type'}`);
    } finally {
      setStorageSwitching(false);
    }
  };

  const updateSetting = async <K extends keyof AppSettings>(
    key: K,
    value: AppSettings[K]
  ) => {
    if (!settings) return;

    setSaving(true);
    try {
      const updated = { ...settings, [key]: value };
      setSettings(updated);
      await storageService.updateSettings({ [key]: value });
      
      // Reload app when visual settings change
      if (key === 'appBackground' || key === 'banglaFont') {
        setTimeout(() => {
          window.location.reload();
        }, 300); // Small delay to allow settings to save
      }
    } catch (error) {
      console.error('Error updating setting:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleLanguageChange = async (nextLang: 'en' | 'bn') => {
    // Apply immediately (header toggle + in-app translations)
    setLang(nextLang);
    // Persist as preference (full settings schema)
    await updateSetting('language', nextLang);
  };

  const handleManualSync = async () => {
    setSyncError('');
    try {
      // Use reconciliation sync for manual sync to ensure completeness
      await syncService.syncWithReconciliation();
    } catch (e: any) {
      setSyncError(e?.message || t('settings_syncFailed'));
    }
  };

  const handleLogout = async () => {
    const confirmed = window.confirm(
      t('settings_confirmLogout')
    );
    if (!confirmed) return;

    // If PIN is enabled, treat this as a session lock so the user can re-enter via PIN.
    // Full logout (token revoke + clearing auth data) is only done when PIN is not enabled.
    const pinEnabledNow = pinEnabled || (await authService.isPinEnabled());
    if (pinEnabledNow) {
      await authService.lock();
      // Go to a protected route so the global PIN gate is shown immediately.
      navigate('/', { replace: true });
      return;
    }

    await authService.logout();
    navigate('/login', { replace: true });
  };

  const handleClearData = async () => {
    const confirmed = window.confirm(
      t('settings_confirmClearData')
    );
    if (confirmed) {
      await storageService.clearAllData();
      navigate('/login');
    }
  };

  const handleDownloadBackup = async () => {
    if (backupBusy) return;
    setBackupBusy(true);
    try {
      const userData = await storageService.getUserData();
      const settingsData = await storageService.getSettings();

      // Filter to only backup current user's person records
      const currentUser = await authService.getCurrentUser();
      const userAny = currentUser as any;
      const surveyorUuid: string | undefined = (typeof userAny?.uuid === 'string' && userAny.uuid.length > 0)
        ? userAny.uuid
        : (currentUser?.id ? String(currentUser.id) : undefined);

      const filteredUserData = {
        ...userData,
        persons: surveyorUuid
          ? (userData.persons || []).filter((p: any) => p.surveyor_id === surveyorUuid)
          : userData.persons,
      };

      const backup = {
        schema: 'app-backup@1',  // Generic schema name for easy rebranding
        created_at: new Date().toISOString(),
        app_version: APP_CONFIG.VERSION,
        payload: {
          userData: filteredUserData,
          settings: settingsData,
        },
      };

      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      await downloadBackupFile(`app-backup_${ts}.json`, backup);  // Generic filename
      window.alert(t('settings_backupCreated'));
    } catch (e) {
      console.error('Backup download failed:', e);
      window.alert(t('settings_backupFailed'));
    } finally {
      setBackupBusy(false);
    }
  };

  const parseBackup = (raw: any): { userData: any; settings?: any } | null => {
    // New format
    if (raw && typeof raw === 'object' && raw.payload && typeof raw.payload === 'object') {
      const userData = (raw.payload as any).userData;
      const settingsData = (raw.payload as any).settings;
      if (userData && typeof userData === 'object' && Array.isArray((userData as any).persons)) {
        return { userData, settings: settingsData };
      }
    }

    // Legacy format: raw userData directly
    if (raw && typeof raw === 'object' && Array.isArray((raw as any).persons)) {
      return { userData: raw };
    }

    return null;
  };

  const handleRestoreBackup = async () => {
    if (restoreBusy) return;

    const file = await pickBackupFile();
    if (!file) return;

    const confirmed = window.confirm(t('settings_confirmRestore'));
    if (!confirmed) return;

    setRestoreBusy(true);
    try {
      const raw = await readBackupFile(file);
      const parsed = parseBackup(raw);
      if (!parsed) {
        window.alert(t('settings_restoreFailed'));
        return;
      }

      await storageService.setUserData(parsed.userData);

      if (parsed.settings && typeof parsed.settings === 'object') {
        // Override settings fully from backup
        await storageService.set(APP_CONFIG.STORAGE_KEYS.SETTINGS, parsed.settings);
      }

      // Force sync status to be recalculated
      await storageService.remove(APP_CONFIG.STORAGE_KEYS.LAST_SYNC);

      window.alert(t('settings_restoreSuccess'));
      window.location.reload();
    } catch (e) {
      console.error('Restore failed:', e);
      window.alert(t('settings_restoreFailed'));
    } finally {
      setRestoreBusy(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">{t('loading')}</div>
      </div>
    );
  }

  return (
    <motion.div className="flex flex-col grow"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}>

      <div className="grow bg-gray-50 pb-6">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="px-scr py-4 flex items-center justify-between">
            <div className="flex items-center">
              <button onClick={() => navigate('/')} className="mr-2">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-gray-800">{t('settings_title')}</h1>
            </div>
            
            {/* Help link */}
            <a
              href={API_CONFIG.HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
              aria-label={t('settings_helpManual')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
              </svg>
              <span className="text-sm font-medium">{t('settings_userGuide')}</span>
            </a>
          </div>
        </div>

        {/* Mini User Profile */}
        <div className="bg-white border-b px-scr py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {currentUser?.avatar ? (
                  <img src={currentUser.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-blue-600 font-bold text-lg">
                    {currentUser ? (currentUser.name || currentUser.username || '?')[0].toUpperCase() : 'G'}
                  </span>
                )}
              </div>
              {/* User Info */}
              <div className="min-w-0">
                <p className="font-semibold text-gray-800 text-lg truncate">
                  {currentUser ? currentUser.name : 'Guest'}
                </p>
                {currentUser ? (
                  <>
                    <p className="text-sm text-blue-600 truncate">@{currentUser.username}</p>
                    <p className="text-sm text-gray-400 truncate">{currentUser.email}</p>
                  </>
                ) : (
                  <p className="text-xs text-gray-400">Offline mode</p>
                )}
              </div>
            </div>
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex-shrink-0 h-9 flex items-center justify-center gap-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-500 transition-colors px-2 sm:px-3"
              aria-label={t('settings_logout')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="flex-shrink-0"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 3c-.93 0-1.395 0-1.776.102a3 3 0 0 0-2.122 2.122C3 5.605 3 6.07 3 7v10c0 .93 0 1.395.102 1.776a3 3 0 0 0 2.122 2.122C5.605 21 6.07 21 7 21m9.5-4.5S21 13.186 21 12s-4.5-4.5-4.5-4.5M20 12H8"/></svg>
              <span className="hidden sm:inline text-sm font-medium">{t('settings_logout')}</span>
            </button>
          </div>
        </div>

        {/* Settings Content */}
        <div className="px-scr py-6 space-y-4">
          {/* Sync Settings - Hidden in offline mode */}
          {!settings?.offlineMode && (
          <div className="bg-white rounded-lg shadow-sm px-4 py-2">
            <h2 className="text-lg font-semibold text-gray-800">
              {t('settings_syncSettings')}
            </h2>

            {/* Manual Sync */}
            <div className="py-3 border-b">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-800">{t('settings_manualSync')}</p>
                  <p className="text-sm text-gray-500">
                    {t('settings_pendingChanges')}: {syncStatus?.pendingChanges ?? 0}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {t('settings_lastSync')}: {syncStatus?.lastSyncAt ? new Date(syncStatus.lastSyncAt).toLocaleString() : t('settings_never')}
                  </p>
                  {syncStatus?.lastSyncResult && !syncStatus.lastSyncResult.success && syncStatus.lastSyncResult.error && (
                    <p className="text-xs text-red-600 mt-1">{syncStatus.lastSyncResult.error}</p>
                  )}
                  {syncError && (
                    <p className="text-xs text-red-600 mt-1">{syncError}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleManualSync}
                  disabled={!!syncStatus?.isSyncing}
                  className="btn-form btn-form-primary text-sm !w-auto px-4 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {syncStatus?.isSyncing ? t('settings_syncing') : t('settings_syncNow')}
                </button>
              </div>

              {syncStatus?.isSyncing && (
                <div className="mt-3">
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${syncStatus.progress ?? 10}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('settings_syncInProgress')}
                  </p>
                </div>
              )}
            </div>

            {/* Auto Sync */}
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <p className="font-medium text-gray-800">{t('settings_autoSync')}</p>
                <p className="text-sm text-gray-500">
                  {t('settings_autoSyncDesc')}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.autoSyncEnabled}
                  onChange={(e) => updateSetting('autoSyncEnabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Sync Interval */}
            <div className="py-2">
              <label className="block font-medium text-gray-800 mb-2">
                {t('settings_syncInterval')}
              </label>
              <input
                type="number"
                min={SYNC_CONFIG.MIN_INTERVAL}
                max={SYNC_CONFIG.MAX_INTERVAL}
                value={settings.syncInterval}
                onChange={(e) => updateSetting('syncInterval', parseInt(e.target.value))}
                className="input-form"
              />
            </div>
          </div>
          )}

          {/* Security Settings */}
          {!settings?.offlineMode && (
            <div className="bg-white rounded-lg shadow-sm px-4 py-2">
              <h2 className="text-lg font-semibold text-gray-800">
                {t('settings_security')}
              </h2>          
              
              <div className="py-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">{t('settings_pinLock')}</p>
                    <p className="text-sm text-gray-500">
                      {t('settings_pinLockDesc')}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {pinLoading
                        ? t('loading')
                        : pinEnabled
                          ? t('settings_pinEnabled')
                          : t('settings_pinNotEnabled')}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pinEnabled}
                      disabled={pinLoading}
                      onChange={(e) => handleTogglePin(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => navigate('/setup-pin')}
                    className="px-4 py-2 bg-purple-700 text-white rounded-xl text-sm font-medium"
                  >
                    {pinEnabled ? t('pin_changePin') : t('pin_setupPin')}
                  </button>
                </div>
              </div>          
            </div>
          )}

          {/* UI Preferences */}
          <div className="bg-white rounded-lg shadow-sm px-4 py-2">
            <h2 className="text-lg font-semibold text-gray-800">
              {t('settings_preferences')}
            </h2>

            {/* Language */}
            <div className="py-2">
              <label className="block font-medium text-gray-800 mb-2">
                {t('settings_language')}
              </label>
              <select
                value={settings.language}
                onChange={(e) => handleLanguageChange(e.target.value as 'en' | 'bn')}
                className="select-form"
              >
                <option value="en">English</option>
                <option value="bn">বাংলা</option>
              </select>
            </div>
          </div>

          {/* Advanced */}
          <div className="bg-white rounded-lg shadow-sm px-4 py-2">
            <h2 className="text-lg font-semibold text-gray-800">
              {t('settings_advanced')}
            </h2>

            {/* Developer Mode */}
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-gray-800">{t('settings_developerMode')}</p>
                <p className="text-sm text-gray-500">
                  {t('settings_developerModeDesc')}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.developerMode}
                  onChange={(e) => updateSetting('developerMode', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Clear Data (Developer Mode only) */}
            {settings.developerMode && (
              <div className="flex flex-col gap-4 pt-4 pb-2 border-t">
                {/* Storage Type Selection */}
                <div className="border-b pb-3">
                  <p className="text-sm font-medium text-gray-800 mb-2">
                    {t('settings_storageType') || 'Data Storage Type'}
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    {t('settings_storageTypeDesc') || 'Choose how your data is stored locally. SQLite provides better performance for large datasets.'}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleStorageTypeChange('localStorage')}
                      disabled={storageSwitching}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        storageType === 'localStorage'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      localStorage
                    </button>
                    <button
                      onClick={() => handleStorageTypeChange('sqlite')}
                      disabled={storageSwitching}
                      className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        storageType === 'sqlite'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      SQLite
                    </button>
                  </div>
                  {storageSwitching && (
                    <p className="text-xs text-blue-600 mt-2">
                      {t('settings_switchingStorage') || 'Switching storage, please wait...'}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleClearData}
                  className="text-sm w-full text-left font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                  {t('settings_clearAllData')}
                </button>
                
                <button
                  onClick={() => navigate('/manage-orphaned-records')}
                  className="text-sm w-full text-left font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  {t('settings_manageOrphanedRecords')}
                </button>

                <button
                  onClick={() => navigate('/view-logs')}
                  className="text-sm w-full text-left font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m-7 5h8a2 2 0 002-2V7a2 2 0 00-2-2H8a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  {t('settings_viewDebugLogs')}
                </button>

                {/* App Background */}
                <div className="border-t pt-3">
                  <p className="text-sm font-medium text-gray-800 mb-2">App Background</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateSetting('appBackground', 'bg1')}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        settings.appBackground === 'bg1'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      BG 1
                    </button>
                    <button
                      onClick={() => updateSetting('appBackground', 'bg2')}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        settings.appBackground === 'bg2'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      BG 2
                    </button>
                    <button
                      onClick={() => updateSetting('appBackground', 'white')}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        settings.appBackground === 'white'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      White
                    </button>
                  </div>
                </div>

                {/* Bangla Font */}
                <div className="border-t pt-3">
                  <p className="text-sm font-medium text-gray-800 mb-2">Bangla Font</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateSetting('banglaFont', 'noto')}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        settings.banglaFont === 'noto'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Noto Sans
                    </button>
                    <button
                      onClick={() => updateSetting('banglaFont', 'lalsabuj')}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        settings.banglaFont === 'lalsabuj'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Lal Sabuj
                    </button>
                    <button
                      onClick={() => updateSetting('banglaFont', 'baloo')}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        settings.banglaFont === 'baloo'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Baloo Da
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Survey Settings */}
          <div className="bg-white rounded-lg shadow-sm px-4 py-2">
            <button
              onClick={() => setSurveySettingsExpanded(!surveySettingsExpanded)}
              className="w-full flex items-center justify-between py-2"
            >
              <h2 className="text-lg font-semibold text-gray-800">
                {t('settings_surveySettings')}
              </h2>
              <svg
                className={`w-5 h-5 transform transition-transform ${surveySettingsExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {surveySettingsExpanded && (
              <div className="space-y-4 pt-2 pb-4">
                <p className="text-sm text-gray-500">
                  {t('settings_surveySettingsDesc')}
                </p>

                {/* Name Field Label */}
                <div className="border-t pt-3">
                  <p className="text-sm font-medium text-gray-800 mb-2">{t('settings_surveyNameFieldLabel')}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateSetting('surveyNameFieldLabel', 'fullNameOrId')}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        (settings.surveyNameFieldLabel ?? 'fullNameOrId') === 'fullNameOrId'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {t('settings_surveyNameFullNameOrId')}
                    </button>
                    <button
                      onClick={() => updateSetting('surveyNameFieldLabel', 'idOnly')}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        (settings.surveyNameFieldLabel ?? 'fullNameOrId') === 'idOnly'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {t('settings_surveyNameIdOnly')}
                    </button>
                  </div>
                </div>

                {/* Field Visibility */}
                <div className="border-t pt-3">
                  <p className="text-sm font-medium text-gray-800 mb-3">{t('settings_surveyFieldVisibility')}</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('settings_surveyShowMobile')}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowMobile ?? false}
                          onChange={(e) => updateSetting('surveyShowMobile', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('settings_surveyShowAge')}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowAge ?? true}
                          onChange={(e) => updateSetting('surveyShowAge', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {(settings.surveyShowAge ?? true) && (
                      <div className="ml-4 space-y-2">
                        <p className="text-xs text-gray-500 mb-2">{t('settings_surveyAgeLimitsDesc')}</p>
                        <div className="flex gap-3 items-center">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings_surveyMinAge')}</label>
                            <input
                              type="number"
                              min="0"
                              max="120"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={settings.surveyMinAge ?? 0}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                if (val >= 0 && val <= 120) updateSetting('surveyMinAge', val);
                              }}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">{t('settings_surveyMaxAge')}</label>
                            <input
                              type="number"
                              min="0"
                              max="120"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              value={settings.surveyMaxAge ?? 120}
                              onChange={(e) => {
                                const val = Number(e.target.value);
                                if (val >= 0 && val <= 120) updateSetting('surveyMaxAge', val);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('settings_surveyShowSex')}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowSex ?? true}
                          onChange={(e) => updateSetting('surveyShowSex', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('settings_surveyShowOccupation')}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowOccupation ?? true}
                          onChange={(e) => updateSetting('surveyShowOccupation', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('settings_surveyShowIncome')}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowIncome ?? false}
                          onChange={(e) => updateSetting('surveyShowIncome', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('settings_surveyShowAddress')}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowAddress ?? false}
                          onChange={(e) => updateSetting('surveyShowAddress', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Meal Logging */}
                <div className="border-t pt-3">
                  <p className="text-sm font-medium text-gray-800 mb-3">{t('settings_mealLogging')}</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{t('settings_restrictMealsByTime')}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.restrictMealsByTime ?? false}
                          onChange={(e) => updateSetting('restrictMealsByTime', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{t('settings_showNutrientOutcome')}</p>
                        <p className="text-xs text-gray-500">{t('settings_showNutrientOutcomeDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={settings.showNutrientOutcome ?? true}
                          onChange={(e) => updateSetting('showNutrientOutcome', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {(settings.showNutrientOutcome ?? true) && (
                      <div className="flex items-center justify-between gap-2 ml-4 mt-2">
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">{t('settings_showNutrientDRI')}</p>
                          <p className="text-xs text-gray-500">{t('settings_showNutrientDRIDesc')}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer ml-2">
                          <input
                            type="checkbox"
                            checked={settings.showNutrientDRI ?? false}
                            onChange={(e) => updateSetting('showNutrientDRI', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    )}
                  </div>
                </div>

          {/* Address Type */}
          {(settings.surveyShowAddress ?? false) && (
            <div className="border-t pt-3">
                    <p className="text-sm font-medium text-gray-800 mb-2">{t('settings_surveyAddressType')}</p>
                    <p className="text-xs text-gray-500 mb-2">
                      {t('settings_surveyAddressTypeDesc')}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => updateSetting('surveyAddressType', 'bangladesh')}
                        className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                          (settings.surveyAddressType ?? 'bangladesh') === 'bangladesh'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {t('settings_surveyAddressBangladesh')}
                      </button>
                      <button
                        onClick={() => updateSetting('surveyAddressType', 'general')}
                        className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                          (settings.surveyAddressType ?? 'bangladesh') === 'general'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {t('settings_surveyAddressGeneral')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Survey UI Settings */}
                <div className="border-t pt-3">
                  <p className="text-sm font-medium text-gray-800 mb-3">{t('settings_surveyUIOptions')}</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700 leading-0">{t('settings_surveyShowBothLanguageNames')}</p>
                        <p className="text-xs text-gray-500">{t('settings_surveyShowBothLanguageNamesDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowBothLanguageNames ?? true}
                          onChange={(e) => updateSetting('surveyShowBothLanguageNames', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{t('settings_surveyOnlyShowRecipesWithImages')}</p>
                        <p className="text-xs text-gray-500">{t('settings_surveyOnlyShowRecipesWithImagesDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={settings.surveyOnlyShowRecipesWithImages ?? true}
                          onChange={(e) => updateSetting('surveyOnlyShowRecipesWithImages', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Extra Information Options */}
                <div className="border-t pt-3">
                  <p className="text-sm font-medium text-gray-800 mb-3">{t('settings_surveyExtraInfoOptions')}</p>

                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">{t('settings_surveyAskExtraInfo')}</p>
                      <p className="text-xs text-gray-500">{t('settings_surveyAskExtraInfoDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-2">
                      <input
                        type="checkbox"
                        checked={settings.surveyAskExtraInfo ?? true}
                        onChange={(e) => updateSetting('surveyAskExtraInfo', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {(settings.surveyAskExtraInfo ?? true) && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{t('settings_surveyShowMealLocation')}</p>
                        <p className="text-xs text-gray-500">{t('settings_surveyShowMealLocationDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowMealLocation ?? true}
                          onChange={(e) => updateSetting('surveyShowMealLocation', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{t('settings_surveyShowFishSource')}</p>
                        <p className="text-xs text-gray-500">{t('settings_surveyShowFishSourceDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowFishSource ?? true}
                          onChange={(e) => updateSetting('surveyShowFishSource', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{t('settings_surveyShowPreparedBy')}</p>
                        <p className="text-xs text-gray-500">{t('settings_surveyShowPreparedByDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowPreparedBy ?? true}
                          onChange={(e) => updateSetting('surveyShowPreparedBy', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{t('settings_surveyShowCoIngredients')}</p>
                        <p className="text-xs text-gray-500">{t('settings_surveyShowCoIngredientsDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowCoIngredients ?? true}
                          onChange={(e) => updateSetting('surveyShowCoIngredients', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{t('settings_surveyShowPeopleSharing')}</p>
                        <p className="text-xs text-gray-500">{t('settings_surveyShowPeopleSharingDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowPeopleSharing ?? true}
                          onChange={(e) => updateSetting('surveyShowPeopleSharing', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{t('settings_surveyShowNotes')}</p>
                        <p className="text-xs text-gray-500">{t('settings_surveyShowNotesDesc')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-2">
                        <input
                          type="checkbox"
                          checked={settings.surveyShowNotes ?? false}
                          onChange={(e) => updateSetting('surveyShowNotes', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Backup & Restore */}
          <div className="bg-white rounded-lg shadow-sm px-4 pt-2 pb-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              {t('settings_backupRestore')}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {t('settings_backupRestoreDesc')}
            </p>

            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={handleDownloadBackup}
                disabled={backupBusy}
                className="btn-form btn-form-primary text-sm !w-auto px-4 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {t('settings_downloadBackup')}
              </button>

              <button
                onClick={handleRestoreBackup}
                disabled={restoreBusy}
                className="btn-form btn-form-secondary text-sm !w-auto px-4 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {restoreBusy ? t('settings_restoring') : t('settings_restoreFromBackup')}
              </button>
            </div>
          </div>

          {/* Logout - now in mini profile section at top */}
          {/* <button
            onClick={handleLogout}
            className="btn-form btn-form-danger"
          >
            {t('settings_logout')}
          </button> */}        
        </div>

        {/* App Info */}
        <div className="text-center text-sm text-gray-500 pt-8">
          <p 
            onClick={handleVersionTap}
            className="cursor-pointer select-none"
          >
            {t('settings_version')} 1.0.0
          </p>
          <p className="mt-1">© 2025 {t("appTitle")}</p>
        </div>

      {modalChildren && (
        <ModalWindow
          isOpen={!!modalChildren}
          onClose={() => setModalChildren(null)}
        >
          {modalChildren}
        </ModalWindow>
      )}
      </div>
    </motion.div>
  );
}

export default SettingsPage;
